import type { TBaseResponse } from "@/models/common.model"
import type { HttpClient } from "@/services/http-common"
import type { Role, CreateRoleDto, UpdateRoleDto } from "@/types/roles"
import { tryCatch } from "@/lib/try-catch"
import ApiServiceFactory from "@/services/api-service-factory"
import { ApiError } from "@/services/http-common"

export class RolesService {
  private httpClient: HttpClient
  private readonly baseUrl: string = "/role"

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  /**
   * Fetch all roles from the API
   */
  async getAll(): Promise<Role[]> {
    try {
      console.log("Fetching roles from:", `${this.baseUrl}/get-all`)
      const response = await this.httpClient.get<any>(`${this.baseUrl}/get-all`)
      
      console.log("Raw API response for roles:", response)
      console.log("Response type:", typeof response)
      console.log("Response keys:", response ? Object.keys(response) : 'No response')
      
      if (!response) {
        console.warn("No response from API")
        return []
      }

      // Verificar si es la estructura esperada {message, value}
      if (response.value && Array.isArray(response.value)) {
        console.log("✅ Found roles in response.value:", response.value.length, "roles")
        if (response.value.length > 0) {
          console.log("First role:", response.value[0])
        }
        return response.value
      }

      // Verificar si la respuesta es directamente un array
      if (Array.isArray(response)) {
        console.log("✅ Response is directly an array:", response.length, "roles")
        if (response.length > 0) {
          console.log("First role:", response[0])
        }
        return response as Role[]
      }

      // Verificar si está en response.data
      if (response.data && Array.isArray(response.data)) {
        console.log("✅ Found roles in response.data:", response.data.length, "roles")
        if (response.data.length > 0) {
          console.log("First role:", response.data[0])
        }
        return response.data
      }

      // Si tiene una propiedad que parece contener roles
      const possibleRolesKeys = ['roles', 'items', 'results']
      for (const key of possibleRolesKeys) {
        if (response[key] && Array.isArray(response[key])) {
          console.log(`✅ Found roles in response.${key}:`, response[key].length, "roles")
          return response[key]
        }
      }

      console.warn("❌ No roles array found in response structure")
      console.warn("Available keys:", Object.keys(response))
      return []
    }
    catch (error) {
      console.error("❌ Error fetching roles:", error)
      if (error instanceof ApiError) {
        throw error
      }
      throw new ApiError("Error fetching roles from API", 500)
    }
  }

  /**
   * Create a new role
   */
  async create(role: CreateRoleDto): Promise<Role> {
    try {
      console.log("Creating role at:", `${this.baseUrl}/create`)
      console.log("Role data being sent:", role)
      const response = await this.httpClient.post<TBaseResponse<Role>>(`${this.baseUrl}/create`, role)
      
      if (!response || !response.value) {
        console.warn("No role data returned from create")
        throw new ApiError("No role data returned", 500)
      }

      const { value, isSuccess, message, statusCode } = response
      
      if (isSuccess === false) {
        console.error("Error creating role:", message, "Status code:", statusCode)
        throw new ApiError(message || "Error creating role", statusCode || 500)
      }

      return value
    }
    catch (error) {
      console.error("Error creating role:", error)
      throw error
    }
  }

  /**
   * Update an existing role
   */
  async update(roleId: string, role: CreateRoleDto): Promise<Role> {
    try {
      console.log("Updating role at:", `${this.baseUrl}/update/${roleId}`)
      console.log("Role data being sent:", role)
      const response = await this.httpClient.put<TBaseResponse<Role>>(`${this.baseUrl}/update/${roleId}`, role)
      
      if (!response || !response.value) {
        console.warn("No role data returned from update")
        throw new ApiError("No role data returned", 500)
      }

      const { value, isSuccess, message, statusCode } = response
      
      if (isSuccess === false) {
        console.error("Error updating role:", message, "Status code:", statusCode)
        throw new ApiError(message || "Error updating role", statusCode || 500)
      }

      return value
    }
    catch (error) {
      console.error("Error updating role:", error)
      throw error
    }
  }

  /**
   * Delete a role
   */
  async delete(roleId: string): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.delete(`${this.baseUrl}/delete/${roleId}`))
      
      if (response.error) {
        console.error("Error deleting role:", response.error)
        throw new ApiError(response.error.message || "Error deleting role", response.error.statusCode || 500)
      }
    }
    catch (error) {
      console.error("Error deleting role:", error)
      if (error instanceof ApiError) {
        throw error
      }
      throw new ApiError("Unexpected error deleting role", 500)
    }
  }
}

export const rolesService = ApiServiceFactory.createService(RolesService, "roles")

export default RolesService 